'use client';

import { useContext, useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';

import { Sun, Moon } from 'react-feather';
import './Navbar.scss';

import { ThemeContext } from '../../ThemeContext';
import { DOCS_URL, DOWNLOAD_URL, HOME_URL,PRICING_URL } from '../../utils/urls';
import Logo from '../Logopng';

const navItems = [
  { path: DOWNLOAD_URL, label: 'Download' },
  { path: PRICING_URL, label: 'Pricing' },
  { path: DOCS_URL, label: 'Docs' },
];

function Navbar({ bannerClosed }) {
  const { theme, setTheme } = useContext(ThemeContext);
  const location = useLocation();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      const scrollableElement = document.querySelector(
        '.content-scrollable-wrapper'
      );
      if (scrollableElement) {
        setIsScrolled(scrollableElement.scrollTop > 50);
      } else {
        setIsScrolled(window.scrollY > 50);
      }
    };

    const scrollableElement = document.querySelector(
      '.content-scrollable-wrapper'
    );
    if (scrollableElement) {
      scrollableElement.addEventListener('scroll', handleScroll);
      return () =>
        scrollableElement.removeEventListener('scroll', handleScroll);
    } else {
      window.addEventListener('scroll', handleScroll);
      return () => window.removeEventListener('scroll', handleScroll);
    }
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      const navbar = document.getElementById('navbarNav');
      const toggleButton = document.getElementById('navbar-toggler');

      if (
        isMenuOpen &&
        navbar &&
        !navbar.contains(event.target) &&
        !toggleButton.contains(event.target)
      ) {
        setIsMenuOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isMenuOpen]);

  useEffect(() => {
    if (isMenuOpen) {
      setIsMenuOpen(false);
    }
    const element = document.querySelector('.content-scrollable-wrapper');
    if (element) {
      element.scrollTo({
        top: 0,
        behavior: 'smooth',
      });
    }
  }, [location]);

  const toggleTheme = () => {
    setTheme(theme === 'light' ? 'dark' : 'light');
  };

  return (
    <header
      className={`
        navbar navbar-expand-lg fixed-top
        ${theme === 'dark' ? 'navbar-dark' : 'navbar-light'}
        ${bannerClosed ? 'banner-closed' : ''}
        ${isScrolled ? 'scrolled' : ''}
      `}
    >
      <div className="container-fluid navbar-container">
        <div className="navbar-content d-flex align-items-center justify-content-between w-100 position-relative">
          {/* Logo */}
          <Link
            className="navbar-brand d-flex align-items-center text-decoration-none"
            to={HOME_URL}
            aria-label="Home"
          >
            <Logo className="navbar-logo" />
          </Link>

          {/* Center Navigation - Desktop */}
          <nav className="navbar-center d-none d-lg-flex position-absolute">
            <ul className="navbar-nav d-flex flex-row align-items-center mb-0 list-unstyled">
              {navItems.map(({ path, label }) => (
                <li className="nav-item" key={path}>
                  <Link
                    className={`nav-link px-3 py-2 text-decoration-none fw-medium rounded ${
                      location.pathname.includes(path) ? 'active' : ''
                    }`}
                    to={path}
                  >
                    {label}
                  </Link>
                </li>
              ))}
            </ul>
          </nav>

          {/* Theme Switch - Desktop */}
          <div className="navbar-actions d-none d-lg-flex">
            <div className="theme-switch-wrapper d-flex align-items-center">
              <Sun
                size={16}
                className={`sun-icon ${theme === 'light' ? 'active' : ''}`}
              />
              <div className="theme-switch-container mx-2 position-relative">
                <input
                  type="checkbox"
                  id="theme-toggle"
                  className="theme-toggle position-absolute opacity-0"
                  checked={theme === 'dark'}
                  onChange={toggleTheme}
                />
                <label
                  htmlFor="theme-toggle"
                  className="theme-toggle-label position-absolute w-100 h-100"
                >
                  <span className="theme-toggle-button position-absolute rounded-circle"></span>
                </label>
              </div>
              <Moon
                size={16}
                className={`moon-icon ${theme === 'dark' ? 'active' : ''}`}
              />
            </div>
          </div>

          {/* Mobile Menu Toggle */}
          <button
            id="navbar-toggler"
            className="navbar-toggler d-lg-none border-0 p-2 rounded"
            type="button"
            aria-label="Toggle navigation"
            onClick={() => setIsMenuOpen(!isMenuOpen)}
            aria-expanded={isMenuOpen}
          >
            <span className="navbar-toggler-icon"></span>
          </button>
        </div>

        {/* Mobile Menu */}
        <div
          className={`navbar-mobile-menu d-lg-none ${isMenuOpen ? 'show' : ''}`}
        >
          <ul className="navbar-nav-mobile list-unstyled mb-3">
            {navItems.map(({ path, label }, index) => (
              <li
                className="nav-item-mobile"
                key={path}
                style={{
                  animationDelay: isMenuOpen ? `${index * 0.1}s` : '0s',
                }}
              >
                <Link
                  className={`nav-link-mobile d-block py-2 px-3 text-decoration-none fw-medium rounded ${
                    location.pathname.includes(path) ? 'active' : ''
                  }`}
                  to={path}
                >
                  {label}
                </Link>
              </li>
            ))}
          </ul>

          <div className="mobile-theme-switch pt-3 border-top d-flex justify-content-center">
            <div className="theme-switch-wrapper d-flex align-items-center">
              <Sun
                size={16}
                className={`sun-icon ${theme === 'light' ? 'active' : ''}`}
              />
              <div className="theme-switch-container mx-2 position-relative">
                <input
                  type="checkbox"
                  id="theme-toggle-mobile"
                  className="theme-toggle position-absolute opacity-0"
                  checked={theme === 'dark'}
                  onChange={toggleTheme}
                />
                <label
                  htmlFor="theme-toggle-mobile"
                  className="theme-toggle-label position-absolute w-100 h-100"
                >
                  <span className="theme-toggle-button position-absolute rounded-circle"></span>
                </label>
              </div>
              <Moon
                size={16}
                className={`moon-icon ${theme === 'dark' ? 'active' : ''}`}
              />
            </div>
          </div>
        </div>
      </div>
    </header>
  );
}

export default Navbar;
